import { api } from '@services';
import { Hotel } from '@types';

/**
 * Service for hotel-related API operations
 */
const hotelService = {
  /**
   * Fetch all hotels
   */
  getHotels: async (): Promise<Hotel[]> => {
    try {
      const response = await api.get('/hotels');
      return response.data;
    } catch (error) {
      console.error('Error fetching hotels:', error);
      throw error;
    }
  },

  /**
   * Fetch a single hotel by ID
   */
  getHotelById: async (hotelId: string, bustCache: boolean = false): Promise<Hotel> => {
    try {
      // Add cache-busting parameters when needed
      const cacheBuster = bustCache ? `?_t=${Date.now()}&r=${Math.random()}` : '';

      console.log(`🔍 Fetching hotel ${hotelId}${bustCache ? ' with cache-busting' : ''}...`);

      const response = await api.get(`/hotels/${hotelId}${cacheBuster}`);

      console.log(`✅ Successfully fetched hotel ${hotelId}:`, {
        estimatedValue: response.data.estimated_value,
        lastValuationDate: response.data.last_valuation_date,
        cacheBusted: bustCache
      });

      return response.data;
    } catch (error: any) {
      console.error(`❌ Error fetching hotel ${hotelId}:`, {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        cacheBusted: bustCache
      });
      throw error;
    }
  },

  /**
   * Delete a hotel by ID
   */
  deleteHotel: async (hotelId: string): Promise<void> => {
    try {
      await api.delete(`/hotels/${hotelId}`);
    } catch (error) {
      console.error('Error deleting hotel:', error);
      throw error;
    }
  },

  /**
   * Create a new hotel
   */
  createHotel: async (hotelData: Partial<Hotel>): Promise<Hotel> => {
    try {
      const response = await api.post('/hotels', hotelData);
      return response.data;
    } catch (error) {
      console.error('Error creating hotel:', error);
      throw error;
    }
  },

  /**
   * Update an existing hotel
   */
  updateHotel: async (hotelId: string, hotelData: Partial<Hotel>): Promise<Hotel> => {
    try {
      const response = await api.put(`/hotels/${hotelId}`, hotelData);
      return response.data;
    } catch (error) {
      console.error('Error updating hotel:', error);
      throw error;
    }
  }
};

export default hotelService;